import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

interface MapLocationPickerProps {
    onClose: () => void;
    initialLat?: number | string;
    initialLng?: number | string;
    onLocationSelect: (lat: number, lng: number) => void;
}

const mapContainerStyle = {
    width: '100%',
    height: '400px',
};

const defaultCenter = {
    lat: -23.5505,
    lng: -46.6333,
};

export function MapLocationPicker({
    onClose,
    initialLat,
    initialLng,
    onLocationSelect,
}: MapLocationPickerProps) {
    const { t } = useTranslation();
    const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [selectedPosition, setSelectedPosition] = useState<{ lat: number; lng: number } | null>(null);
    const [isLocating, setIsLocating] = useState(false);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    });

    useEffect(() => {
        if (navigator.geolocation) {
            setIsLocating(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setUserLocation(location);
                    setIsLocating(false);
                },
                (error) => {
                    console.error("Error getting location:", error);
                    setIsLocating(false);
                    if (initialLat && initialLng) {
                        const lat = typeof initialLat === 'string' ? parseFloat(initialLat) : initialLat;
                        const lng = typeof initialLng === 'string' ? parseFloat(initialLng) : initialLng;
                        if (!isNaN(lat) && !isNaN(lng)) {
                            setSelectedPosition({ lat, lng });
                            setUserLocation({ lat, lng });
                        }
                    }
                }
            );
        } else if (initialLat && initialLng) {
            const lat = typeof initialLat === 'string' ? parseFloat(initialLat) : initialLat;
            const lng = typeof initialLng === 'string' ? parseFloat(initialLng) : initialLng;
            if (!isNaN(lat) && !isNaN(lng)) {
                setSelectedPosition({ lat, lng });
                setUserLocation({ lat, lng });
            }
        }
    }, [initialLat, initialLng]);

    useEffect(() => {
        if (initialLat && initialLng) {
            const lat = typeof initialLat === 'string' ? parseFloat(initialLat) : initialLat;
            const lng = typeof initialLng === 'string' ? parseFloat(initialLng) : initialLng;
            if (!isNaN(lat) && !isNaN(lng)) {
                setSelectedPosition({ lat, lng });
            }
        }
    }, [initialLat, initialLng]);

    const onMapClick = (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const position = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
            };
            setSelectedPosition(position);
        }
    };


    const handleConfirm = () => {
        if (selectedPosition) {
            onLocationSelect(selectedPosition.lat, selectedPosition.lng);
            onClose();
        } else {
            alert(t('select_location_first'));
        }
    };

    if (loadError) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="error">{t('map_load_error')}</Typography>
            </Box>
        );
    }

    if (!isLoaded || isLocating) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography>{isLocating ? t('LOCATION_TIMEOUT_TITLE') : t('loading_map')}</Typography>
            </Box>
        );
    }


    const center = selectedPosition || userLocation || defaultCenter;

    return (
        <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', borderRadius: 2, overflow: "hidden" }}>
            <GoogleMap
                zoom={14}
                center={center}
                onClick={onMapClick}
                mapContainerStyle={mapContainerStyle}
                options={{ zoomControl: true, colorScheme: 'DARK', mapTypeControl: false, fullscreenControl: true, streetViewControl: false }}
            >
                {selectedPosition && (
                    <Marker
                        position={selectedPosition}
                        draggable={true}
                        onDragEnd={(e) => {
                            if (e.latLng) {
                                setSelectedPosition({
                                    lat: e.latLng.lat(),
                                    lng: e.latLng.lng(),
                                });
                            }
                        }}
                    />
                )}
            </GoogleMap>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={onClose}>
                    {t('cancel')}
                </Button>
                <Button variant="contained" onClick={handleConfirm}>
                    {t('confirm_location')}
                </Button>
            </Box>

            {selectedPosition && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    {t('coordinates')}: {selectedPosition.lat.toFixed(6)}, {selectedPosition.lng.toFixed(6)}
                </Typography>
            )}
        </Box>
    );
}